import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState } from '@reduxjs/toolkit';
import { PromotionInfo } from '../models/promotionInfo';
import { PromotionService } from '../services';
import { exceptionOf, SerializedException } from '../models';
import { AppState } from './index';

export const PROMOTION_CAMPAIGN_FEATURE_KEY = 'promotionCampaign';

interface PromotionCampaignState extends EntityState<PromotionInfo> {
  loading: boolean;
  errors: SerializedException[];
}

const promotionCampaignAdapter = createEntityAdapter<PromotionInfo>();

const createInitialState = (partialState: Partial<PromotionCampaignState> = {}) => ({
  ...partialState,
  loading: false,
  errors: [] as SerializedException[],
});

export const doGetPromotionCampaign = createAsyncThunk(
  'promotionCampaign/getPromotionCampaign',
  async (accountId: number, { rejectWithValue }) => {
    try {
      const data = await PromotionService.getActivePromotionCampaign(accountId);
      return { promotionCampaign: data };
    } catch (e: any) {
      return rejectWithValue(exceptionOf(e).toJson());
    }
  },
);

const promotionCampaignSlice = createSlice({
  name: PROMOTION_CAMPAIGN_FEATURE_KEY,
  initialState: promotionCampaignAdapter.getInitialState(createInitialState()),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(doGetPromotionCampaign.pending, state => {
      state.loading = true;
    });
    builder.addCase(doGetPromotionCampaign.fulfilled, (state, action) => {
      state.loading = false;
      promotionCampaignAdapter.upsertOne(state, action.payload.promotionCampaign);
    });
    builder.addCase(doGetPromotionCampaign.rejected, (state, action) => {
      state.loading = false;
      state.errors.push(action.payload as SerializedException);
    });
  },
});

const selectPromotionCampaignFeature = (state: AppState) => state[PROMOTION_CAMPAIGN_FEATURE_KEY];
const { selectAll } = promotionCampaignAdapter.getSelectors(selectPromotionCampaignFeature);

export const selectPromotionCampaign = createSelector(
  selectAll,
  all => all[0],
);

export default promotionCampaignSlice.reducer;
